/**
 * Lien « Aide » dans l'en-tête des écrans de la boucle de l'argent (audit du
 * 20/10/2026, segment 8 : « Les pages 77, 91, 9, 81 et 90 n'ont ni Aide, ni
 * Se former, ni ? »). Il ouvre Assistance sur LA question de l'écran.
 *
 *   77 Collecte des offrandes     → « Où saisir l'offrande d'une messe ? »
 *   91 Encaissements au guichet   → « Comment encaisser une somme au guichet ? »
 *    9 Clôture de la semaine      → « Qui valide la clôture de la semaine ? »
 *   81 Dons en ligne              → « Comment voir les dons en ligne en direct… ? »
 *   90 Mes reversements           → « Comment déclarer un versement à l'Économat ? »
 *
 * Les écrans eux-mêmes ne sont pas modifiés (la clôture, la collecte et Mes
 * reversements appartiennent à un autre lot du 20/10) : `AideEnTete`, monté
 * une fois dans le cadre de l'application, place le lien sous le titre de
 * l'écran (`data-testid="text-page-title"`), dans son bloc d'en-tête, par un
 * portail React. Un écran qui pose lui-même `<LienAide>` dans son en-tête
 * (`data-lien-aide`) n'en reçoit pas un second.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import { HelpCircle } from "lucide-react";
import { useTranslation } from "@/lib/i18n/i18n";

/** Écran (route) → question d'Assistance (`common.assistance.faq.<id>`). */
export const AIDE_PAR_ECRAN: Readonly<Record<string, string>> = {
  "/collecte": "offrandesMesses",
  "/encaissements": "guichet",
  "/cloture": "validation",
  "/mobile-money": "donsEnDirect",
  "/reversements": "declarerVersement",
};

export function lienAssistance(question: string): string {
  return `/assistance?question=${encodeURIComponent(question)}`;
}

/** Le lien lui-même, lisible sur un bandeau bleu comme sur un fond clair. */
export function LienAide({ question }: { question: string }) {
  const t = useTranslation();
  return (
    <Link
      href={lienAssistance(question)}
      className="mt-1 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-semibold text-brand-primary shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
      data-testid="lien-aide"
      data-lien-aide={question}
      aria-label={t("common.aide.lienTitre", { question: t(`common.assistance.faq.${question}.q`) })}
      title={t(`common.assistance.faq.${question}.q`)}
    >
      <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
      {t("common.aide.lien")}
    </Link>
  );
}

/** Bloc d'en-tête de l'écran : le parent de son titre. */
function blocDuTitre(): HTMLElement | null {
  const racine = document.getElementById("main") ?? document.body;
  const titre = racine.querySelector<HTMLElement>('[data-testid="text-page-title"]');
  return titre?.parentElement ?? null;
}

export function AideEnTete() {
  const [location] = useLocation();
  const question = AIDE_PAR_ECRAN[location.split("?")[0]] ?? null;
  const [hote, setHote] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!question || typeof document === "undefined") {
      setHote(null);
      return;
    }
    let conteneur: HTMLElement | null = null;
    const placer = () => {
      if (conteneur?.isConnected) return;
      const bloc = blocDuTitre();
      // Écran pas encore affiché (chargement), ou qui porte déjà son lien.
      if (!bloc || bloc.querySelector("[data-lien-aide]")) {
        if (conteneur && !conteneur.isConnected) setHote(null);
        return;
      }
      conteneur = document.createElement("div");
      conteneur.setAttribute("data-aide-en-tete", question);
      bloc.appendChild(conteneur);
      setHote(conteneur);
    };
    placer();
    const observateur = new MutationObserver(placer);
    observateur.observe(document.getElementById("main") ?? document.body, { childList: true, subtree: true });
    return () => {
      observateur.disconnect();
      conteneur?.remove();
      setHote(null);
    };
  }, [question, location]);

  if (!question || !hote) return null;
  return createPortal(<LienAide question={question} />, hote);
}

export default AideEnTete;
